import { useState, useEffect, useMemo } from "react";
import { X, Plus, ArrowUpDown, AlertTriangle } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useContactData } from '@/hooks/useContactData';
import { useContactOptions } from '@/hooks/useContactOptions';
import { useCustomFields } from '@/hooks/useCustomFields';
import { useWorkspaces } from '@/hooks/useWorkspaces';
import {
  customFieldToFilterOption,
  type FilterFieldOption,
} from '@/lib/filters/types';
import { DateFilterInput } from './DateFilterInput';
import { Combobox } from '@/components/ui/combobox';
import { Checkbox } from '@/components/ui/checkbox';
import { computeAbsoluteDateFromRelative, type RelativeDirection } from '@/lib/filters/relative-dates';
import { validateFilterBlocks } from '@/lib/filters/validator';
import { captureError } from '@/lib/sentry';
import { SORTABLE_FIELDS, type SortField } from '@/types/contact';

// Named sort presets surfaced in the filter dialog. The Rotation preset is
// also the system default (matched by the RPC fallback when no sort_config
// is supplied) — leaving the staged sort empty produces the same row order
// as picking Rotation explicitly, so detectSortPreset() treats `[]` as
// 'rotation' to keep the UI in sync with backend behavior.
const SORT_PRESETS: Record<string, { label: string; entries: SortField[] }> = {
  rotation: {
    label: 'Rotation (next contact)',
    entries: [{ field: 'next_contact', direction: 'ASC', nulls: 'first' }],
  },
  recentContact: {
    label: 'Last Contacted (Newest first)',
    entries: [{ field: 'last_contacted', direction: 'DESC' }],
  },
  oldestContact: {
    label: 'Last Contacted (Oldest first)',
    entries: [{ field: 'last_contacted', direction: 'ASC' }],
  },
  // Daily call filter: the call_priority tier bucket (past/today activity →
  // Referral-Prospect-NYC → Prospect-A → Prospect-B → Client-due → Referrer-NYC), then
  // next-contact date within each tier. Entries must match the Daily preset's sort in
  // FilterNavMenu exactly for detectSortPreset() to recognize it and show "Daily priority"
  // instead of "Custom".
  daily: {
    label: 'Daily priority (tier → next contact)',
    entries: [
      { field: 'call_priority', direction: 'ASC' },
      // Must stay identical to the Daily preset's sort in FilterNavMenu (see nulls note there),
      // or detectSortPreset() stops recognising it and the UI shows "Custom".
      { field: 'next_contact', direction: 'ASC', nulls: 'first' },
    ],
  },
};

function detectSortPreset(stagedSort: SortField[]): string {
  // Empty staged sort relies on the server's rotation fallback — show the
  // user that's what they'll get, rather than leaving the selector blank.
  if (stagedSort.length === 0) return 'rotation';
  for (const [key, preset] of Object.entries(SORT_PRESETS)) {
    if (stagedSort.length !== preset.entries.length) continue;
    const match = stagedSort.every((s, i) => {
      const target = preset.entries[i];
      return (
        s.field === target.field &&
        s.direction === target.direction &&
        (s.nulls ?? null) === (target.nulls ?? null)
      );
    });
    if (match) return key;
  }
  return 'custom';
}

interface FilterCondition {
  id: string;
  table: string;
  field: string;
  operator: string;
  value: string;
  valueEnd?: string;
  // For important_dates.date: also require this label on the SAME row (correlated).
  label?: string;
  dateMode?: 'absolute' | 'relative';
  relativeValue?: number;
  relativeUnit?: 'days';
  relativeDirection?: RelativeDirection;
  relativeValueEnd?: number;
  relativeDirectionEnd?: RelativeDirection;
  // For important_dates.date: when true, compare full dates (year-aware).
  // When false/undefined (default), filter_contacts compares (month, day) only.
  matchYear?: boolean;
}

interface FilterBlock {
  id: string;
  type: 'AND' | 'OR';
  conditions: FilterCondition[];
}

interface ContactFiltersProps {
  onFilterChange: (filters: any) => void;
  currentFilters: any;
}

const STANDARD_FILTER_FIELDS: FilterFieldOption[] = [
  { value: 'contacts.first_name', label: 'First Name', table: 'contacts', type: 'text' },
  { value: 'contacts.last_name', label: 'Last Name', table: 'contacts', type: 'text' },
  { value: 'contacts.type', label: 'Type', table: 'contacts', type: 'text' },
  { value: 'contacts.rank', label: 'Rank', table: 'contacts', type: 'text' },
  { value: 'contacts.status', label: 'Status', table: 'contacts', type: 'text' },
  { value: 'contacts.relationship', label: 'Relationship', table: 'contacts', type: 'text' },
  { value: 'contacts.remarket', label: 'Remarket', table: 'contacts', type: 'text' },
  { value: 'groups.group_name', label: 'Group Name', table: 'groups', type: 'text' },
  { value: 'contacts.company_name', label: 'Company', table: 'contacts', type: 'text' },
  { value: 'contacts.job_title', label: 'Job Title', table: 'contacts', type: 'text' },
  { value: 'contacts.industry', label: 'Industry', table: 'contacts', type: 'text' },
  { value: 'contacts.source', label: 'Source', table: 'contacts', type: 'text' },
  { value: 'contacts.import_batch', label: 'Import Batch', table: 'contacts', type: 'text' },
  { value: 'contacts.created_at', label: 'Created Date', table: 'contacts', type: 'datetime' },
  { value: 'contacts.imported_at', label: 'Imported At', table: 'contacts', type: 'datetime' },
  { value: 'contacts.next_contact', label: 'Next Contact', table: 'contacts', type: 'datetime' },
  { value: 'contacts.last_contacted', label: 'Last Contacted', table: 'contacts', type: 'datetime' },
  { value: 'email_address.email', label: 'Email', table: 'email_address', type: 'text' },
  { value: 'email_address.type', label: 'Email Type', table: 'email_address', type: 'text' },
  { value: 'email_address.created_at', label: 'Email Created At', table: 'email_address', type: 'datetime' },
  { value: 'physical_addresses.city', label: 'City', table: 'physical_addresses', type: 'text' },
  { value: 'physical_addresses.state', label: 'State', table: 'physical_addresses', type: 'text' },
  { value: 'physical_addresses.postal_code', label: 'Zip / Postal code', table: 'physical_addresses', type: 'text' },
  { value: 'physical_addresses.country', label: 'Country', table: 'physical_addresses', type: 'text' },
  { value: 'contact_interests.interest', label: 'Interest', table: 'contact_interests', type: 'text' },
  { value: 'contact_interests.created_at', label: 'Interest Created At', table: 'contact_interests', type: 'datetime' },
  { value: 'important_dates.label', label: 'Important Date Label', table: 'important_dates', type: 'text' },
  { value: 'important_dates.date', label: 'Important Date', table: 'important_dates', type: 'datetime' },
  { value: 'activities.call_outcome', label: 'Call Outcome', table: 'activities', type: 'text' },
  // Rotation v2 membership field (computed in filter_contacts): contact has a scheduled,
  // not-yet-completed activity in [today, today+3]. Meaningful operators: Is Empty / Is Not
  // Empty (the fixed window means date comparisons behave as "has an event").
  { value: 'activities.next_scheduled_event', label: 'Upcoming Event (≤3 days)', table: 'activities', type: 'existence' },
  // Daily membership (computed in filter_contacts): contact matches a Daily tier — a past/today
  // scheduled uncompleted activity, OR a Referral prospect not yet contacted, OR Prospect rank
  // A/B, OR an Active Client who is due (next_contact today-or-past, NULL counts as due), OR a
  // Referrer not yet contacted. Meaningful operators: Is Empty / Is Not Empty.
  { value: 'contacts.daily_tier_member', label: 'Daily list eligible', table: 'contacts', type: 'existence' },
];

const OPERATORS = [
  { value: 'eq', label: 'Equals', applicableTypes: ['text', 'number', 'boolean'] },
  { value: 'neq', label: 'Not Equal', applicableTypes: ['text', 'number'] },
  { value: 'like', label: 'Contains (Case Sensitive)', applicableTypes: ['text'] },
  { value: 'ilike', label: 'Contains (Case Insensitive)', applicableTypes: ['text'] },
  { value: 'starts_with', label: 'Starts With', applicableTypes: ['text'] },
  { value: 'ends_with', label: 'Ends With', applicableTypes: ['text'] },
  { value: 'gt', label: 'Greater Than', applicableTypes: ['number'] },
  { value: 'gte', label: 'Greater Than or Equal', applicableTypes: ['number'] },
  { value: 'lt', label: 'Less Than', applicableTypes: ['number'] },
  { value: 'lte', label: 'Less Than or Equal', applicableTypes: ['number'] },
  { value: 'is', label: 'Is', applicableTypes: ['text'] },
  { value: 'is_empty', label: 'Is Empty', applicableTypes: ['text', 'datetime', 'number', 'existence'] },
  { value: 'is_not_empty', label: 'Is Not Empty', applicableTypes: ['text', 'datetime', 'number', 'existence'] },
  { value: 'before', label: 'Before', applicableTypes: ['datetime'] },
  { value: 'after', label: 'After', applicableTypes: ['datetime'] },
  { value: 'lte', label: 'On or Before', applicableTypes: ['datetime'] },
  { value: 'gte', label: 'On or After', applicableTypes: ['datetime'] },
  { value: 'between', label: 'Between', applicableTypes: ['datetime', 'number'] },
];

export function ContactFilters({ onFilterChange, currentFilters }: ContactFiltersProps) {
  const [stagedFilterBlocks, setStagedFilterBlocks] = useState<FilterBlock[]>(currentFilters.groups || []);
  const [topGroupType, setTopGroupType] = useState<'AND' | 'OR'>(currentFilters.group_type || 'AND');
  const [stagedSort, setStagedSort] = useState<SortField[]>(currentFilters.sort || []);
  // True once the user has clicked "Apply Filters" at least once. Used to
  // gate inline validation errors in DateFilterInput so they don't flash on
  // initial render.
  const [applyAttempted, setApplyAttempted] = useState(false);
  const { toast } = useToast();
  const { groupNames, interestNames, importantDateLabels } = useContactData();
  const { options: contactOptions } = useContactOptions();

  // Fetch workspace and custom fields
  const { currentWorkspace } = useWorkspaces();
  const { customFields } = useCustomFields(currentWorkspace?.id);

  // Combine standard fields with custom fields
  const allFilterFields = useMemo(() => {
    const customFilterFields = customFields.map(customFieldToFilterOption);
    return [...STANDARD_FILTER_FIELDS, ...customFilterFields];
  }, [customFields]);

  // Map of field keys to autocomplete options
  const autocompleteOptionsMap = useMemo<Record<string, string[]>>(() => ({
    'contacts.type': contactOptions.type || [],
    'contacts.rank': contactOptions.rank || [],
    'contacts.status': contactOptions.status || [],
    'contacts.relationship': contactOptions.relationship || [],
    'contacts.remarket': ['YES', 'NO', 'unsubscribe'],
    'groups.group_name': groupNames,
    'contact_interests.interest': interestNames,
    'important_dates.label': importantDateLabels,
    'activities.call_outcome': contactOptions.call_outcome || [],
  }), [contactOptions, groupNames, interestNames, importantDateLabels]);

  useEffect(() => {
    if (currentFilters.groups) {
      setStagedFilterBlocks(currentFilters.groups);
    }
    if (currentFilters.group_type) {
      setTopGroupType(currentFilters.group_type);
    }
    setStagedSort(currentFilters.sort || []);
  }, [currentFilters]);

  const getFieldType = (fieldValue: string) => {
    const field = allFilterFields.find((f) => f.value === fieldValue);
    return field?.type || 'text';
  };

  /**
   * Checks if a field value represents a custom field
   */
  const isCustomFieldValue = (fieldValue: string) => {
    return fieldValue.startsWith('custom_field:');
  };

  const addFilterBlock = () => {
    setStagedFilterBlocks([
      ...stagedFilterBlocks,
      {
        id: crypto.randomUUID(),
        type: 'AND',
        conditions: [
          {
            id: crypto.randomUUID(),
            table: 'contacts',
            field: 'first_name',
            operator: 'eq',
            value: '',
          },
        ],
      },
    ]);
  };

  const addCondition = (blockId: string) => {
    setStagedFilterBlocks(
      stagedFilterBlocks.map((block) =>
        block.id === blockId
          ? {
              ...block,
              conditions: [
                ...block.conditions,
                {
                  id: crypto.randomUUID(),
                  table: 'contacts',
                  field: 'first_name',
                  operator: 'eq',
                  value: '',
                },
              ],
            }
          : block
      )
    );
  };

  const updateCondition = (
    blockId: string,
    conditionId: string,
    field: keyof FilterCondition | Partial<FilterCondition>,
    value?: string | object
  ) => {
    setStagedFilterBlocks(
      stagedFilterBlocks.map((block) =>
        block.id === blockId
          ? {
              ...block,
              conditions: block.conditions.map((condition) =>
                condition.id === conditionId
                  ? // Handle object update (multiple fields at once)
                    typeof field === 'object'
                    ? { ...condition, ...field }
                    : // Handle single field update
                    field === 'field'
                    ? (() => {
                        const stringValue = String(value);
                        const fieldType = getFieldType(stringValue);
                        // Handle custom fields (format: custom_field:UUID)
                        if (isCustomFieldValue(stringValue)) {
                          return {
                            ...condition,
                            table: 'custom_field',
                            field: stringValue,
                            operator: fieldType === 'datetime' ? 'before' : fieldType === 'boolean' ? 'eq' : 'eq',
                            value: fieldType === 'boolean' ? 'true' : '',
                            valueEnd: undefined,
                            dateMode: fieldType === 'datetime' ? ('absolute' as const) : undefined,
                            relativeUnit: fieldType === 'datetime' ? ('days' as const) : undefined,
                            relativeDirection: fieldType === 'datetime' ? ('ago' as RelativeDirection) : undefined,
                            matchYear: undefined,
                            label: undefined,
                          };
                        }
                        // Handle standard fields (format: table.field)
                        return {
                          ...condition,
                          table: stringValue.split('.')[0],
                          field: stringValue.split('.')[1],
                          operator: fieldType === 'datetime' ? 'before' : fieldType === 'existence' ? 'is_not_empty' : 'eq',
                          value: '',
                          valueEnd: undefined,
                          dateMode: fieldType === 'datetime' ? ('absolute' as const) : undefined,
                          relativeUnit: fieldType === 'datetime' ? ('days' as const) : undefined,
                          relativeDirection: fieldType === 'datetime' ? ('ago' as RelativeDirection) : undefined,
                          matchYear: undefined,
                          label: undefined,
                        };
                      })()
                    : field === 'value'
                    ? {
                        ...condition,
                        value: typeof value === 'object' ? JSON.stringify(value) : String(value),
                        valueEnd: condition.operator === 'between' ? condition.valueEnd : undefined
                      }
                    : field === 'valueEnd'
                    ? {
                        ...condition,
                        valueEnd: typeof value === 'string' ? value : undefined
                      }
                    : { ...condition, [field]: String(value) }
                  : condition
              ),
            }
          : block
      )
    );
  };

  const removeCondition = (blockId: string, conditionId: string) => {
    setStagedFilterBlocks(
      stagedFilterBlocks
        .map((block) =>
          block.id === blockId
            ? {
                ...block,
                conditions: block.conditions.filter((condition) => condition.id !== conditionId),
              }
            : block
        )
        .filter((block) => block.conditions.length > 0)
    );
  };

  const removeBlock = (blockId: string) => {
    setStagedFilterBlocks(stagedFilterBlocks.filter((block) => block.id !== blockId));
  };

  const applyFilters = () => {
    // Mark Apply as attempted so DateFilterInput can show inline errors.
    setApplyAttempted(true);
    try {
      // First, enhance conditions by computing absolute dates for relative filters.
      // The relative-value inputs use placeholder="0" — users routinely treat that
      // as a pre-filled default and click Apply without typing, leaving the field
      // undefined. We coalesce missing relativeValue/Direction to the placeholder's
      // implicit promise (0 days ago) so the validator doesn't silently drop the
      // condition with missing-relative-value.
      const enhancedBlocks = stagedFilterBlocks.map((block) => ({
        ...block,
        conditions: block.conditions.map((c) => {
          if (c.dateMode === 'relative') {
            const unit = c.relativeUnit || 'days';
            const relVal = c.relativeValue ?? 0;
            const relDir = c.relativeDirection ?? 'ago';
            const computedValue = computeAbsoluteDateFromRelative(relVal, unit, relDir);
            if (c.operator === 'between') {
              const relValEnd = c.relativeValueEnd ?? 0;
              const dirEnd = c.relativeDirectionEnd ?? relDir;
              const computedEnd = computeAbsoluteDateFromRelative(relValEnd, unit, dirEnd);
              return { ...c, value: computedValue, valueEnd: computedEnd, relativeUnit: unit };
            }
            return { ...c, value: computedValue, relativeUnit: unit };
          }
          return c;
        }),
      }));

      // Run the validator. Returns surviving blocks + a per-drop report so we
      // can show partial / full-failure toasts and surface each drop to
      // GlitchTip. Replaces the previous inline filter chain (which dropped
      // conditions silently via console.warn).
      const result = validateFilterBlocks(enhancedBlocks);

      // GlitchTip: one event per dropped condition. PII (value, valueEnd) is
      // intentionally NOT included — dates of birth and other date fields
      // can be sensitive.
      for (const d of result.dropped) {
        const err = new Error(`Filter condition dropped: ${d.reason}`);
        captureError(err, {
          feature: 'contact-filter-validate',
          table: d.table,
          field: d.field,
          operator: d.operator,
          reason: d.reason,
        });
      }

      // All-dropped: do NOT call onFilterChange. Preserving the previous
      // filter state is what keeps chips from vanishing on a silent failure.
      if (!result.anyApplied) {
        const detail = result.dropped.map((d) => d.humanReason).join('; ');
        toast({
          title: 'No filters were applied',
          description: result.dropped.length > 0
            ? `All conditions were incomplete: ${detail}. Fix the highlighted fields and try again.`
            : 'Add at least one condition to filter.',
          variant: 'destructive',
        });
        return;
      }

      // NOTE: camelCase → snake_case translation (valueEnd → end_value,
      // matchYear → match_year, etc.) happens centrally at the RPC boundary
      // in `toRpcCondition` / `toRpcFilters` (src/lib/filters/rpc.ts), invoked
      // by `useContacts`. Saved views continue to store the client shape.
      const updatedFilters = {
        ...currentFilters,
        group_type: topGroupType,
        groups: result.blocks,
        sort: stagedSort.length > 0 ? stagedSort : undefined,
      };

      console.log('[ContactFilters] Applying filters:', JSON.stringify(updatedFilters, null, 2));
      onFilterChange(updatedFilters);

      if (result.allValid) {
        toast({
          description: 'Filters applied successfully',
        });
      } else {
        // Partial drop: some conditions made it through, some didn't.
        const detail = result.dropped.map((d) => d.humanReason).join(', ');
        toast({
          title: "Some filters couldn't be applied",
          description: `Skipped: ${detail}. Showing results for the rest.`,
        });
      }
    } catch (err) {
      console.error('[ContactFilters] Error applying filters:', err);
      captureError(err as Error, { feature: 'contact-filter-apply' });
      toast({
        title: 'Error',
        description: 'Failed to apply filters',
        variant: 'destructive',
      });
    }
  };

  const resetFilters = () => {
    setStagedFilterBlocks([]);
    setTopGroupType('AND');
    setStagedSort([]);
    onFilterChange({
      ...currentFilters,
      group_type: 'AND',
      groups: [],
      sort: undefined,
    });
    toast({
      description: 'Filters reset successfully',
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Active Filters</h3>
        <div className="flex items-center gap-2">
          <Select
            value={topGroupType}
            onValueChange={(value: 'AND' | 'OR') => setTopGroupType(value)}
          >
            <SelectTrigger className="w-[100px]">
              <SelectValue placeholder="Group Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="AND">AND</SelectItem>
              <SelectItem value="OR">OR</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={addFilterBlock}>
            <Plus className="h-4 w-4 mr-2" />
            Add Filter Block
          </Button>
        </div>
      </div>

      <div className="space-y-4">
        {stagedFilterBlocks.map((block, blockIndex) => (
          <div key={block.id} className="border rounded-lg p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Select
                  value={block.type}
                  onValueChange={(value) => {
                    setStagedFilterBlocks(
                      stagedFilterBlocks.map((b) =>
                        b.id === block.id ? { ...b, type: value as 'AND' | 'OR' } : b
                      )
                    );
                  }}
                >
                  <SelectTrigger className="w-[100px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AND">AND</SelectItem>
                    <SelectItem value="OR">OR</SelectItem>
                  </SelectContent>
                </Select>
                {blockIndex > 0 && <Badge variant="secondary">Block {blockIndex + 1}</Badge>}
              </div>
              <Button variant="ghost" size="sm" onClick={() => removeBlock(block.id)}>
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="space-y-2">
              {block.conditions.map((condition) => {
                // For custom fields, the field stores the full value (custom_field:UUID)
                // For standard fields, we need to construct the value from table.field
                const isCustomField = condition.table === 'custom_field';
                const fieldValue = isCustomField
                  ? condition.field // Already in custom_field:UUID format
                  : `${condition.table}.${condition.field}`;
                const fieldType = getFieldType(fieldValue);
                const isBetween = condition.operator === 'between';
                const isEmptyOperator = condition.operator === 'is_empty' || condition.operator === 'is_not_empty';
                const isBoolean = fieldType === 'boolean';
                const isNumber = fieldType === 'number';
                const isDatetime = fieldType === 'datetime';
                // Recurring-date opt-in: only relevant for important_dates.date.
                // Hidden for is_empty/is_not_empty (presence checks ignore the year).
                const showMatchYearToggle =
                  condition.table === 'important_dates' &&
                  condition.field === 'date' &&
                  !isEmptyOperator;

                // Separate custom fields from standard fields for grouped display
                const customFieldOptions = allFilterFields.filter(f => f.isCustomField);
                const hasCustomFields = customFieldOptions.length > 0;

                return (
                  <div key={condition.id} className="flex items-center space-x-2">
                    <Select
                      value={fieldValue}
                      onValueChange={(value) => updateCondition(block.id, condition.id, 'field', value)}
                    >
                      <SelectTrigger className="w-[180px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectLabel>Standard Fields</SelectLabel>
                          {STANDARD_FILTER_FIELDS.map((field) => (
                            <SelectItem key={field.value} value={field.value}>
                              {field.label}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                        {hasCustomFields && (
                          <>
                            <SelectSeparator />
                            <SelectGroup>
                              <SelectLabel>Custom Fields</SelectLabel>
                              {customFieldOptions.map((field) => (
                                <SelectItem key={field.value} value={field.value}>
                                  {field.label}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          </>
                        )}
                      </SelectContent>
                    </Select>

                    <Select
                      value={condition.operator}
                      onValueChange={(value) => updateCondition(block.id, condition.id, 'operator', value)}
                    >
                      <SelectTrigger className="w-[180px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {OPERATORS.filter((op) => op.applicableTypes.includes(fieldType)).map((op) => (
                          <SelectItem key={op.value} value={op.value}>
                            {op.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {/* Value input - type depends on field type, hidden for is_empty/is_not_empty */}
                    {isEmptyOperator ? (
                      /* No value input needed for is_empty/is_not_empty operators */
                      <div className="flex-1" />
                    ) : isBoolean ? (
                      <Select
                        value={condition.value || 'true'}
                        onValueChange={(value) => updateCondition(block.id, condition.id, 'value', value)}
                      >
                        <SelectTrigger className="flex-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="true">Yes</SelectItem>
                          <SelectItem value="false">No</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : isDatetime ? (
                      <DateFilterInput
                        mode={condition.dateMode || 'absolute'}
                        onModeChange={(mode) => {
                          updateCondition(block.id, condition.id, { dateMode: mode });
                        }}
                        absoluteValue={condition.value || ''}
                        onAbsoluteValueChange={(value) => {
                          updateCondition(block.id, condition.id, { value });
                        }}
                        relativeValue={condition.relativeValue}
                        onRelativeValueChange={(value) => {
                          updateCondition(block.id, condition.id, { relativeValue: value });
                        }}
                        relativeDirection={condition.relativeDirection || 'ago'}
                        onRelativeDirectionChange={(direction) => {
                          updateCondition(block.id, condition.id, { relativeDirection: direction });
                        }}
                        isBetween={isBetween}
                        absoluteValueEnd={condition.valueEnd || ''}
                        onAbsoluteValueEndChange={(value) => {
                          updateCondition(block.id, condition.id, { valueEnd: value });
                        }}
                        relativeValueEnd={condition.relativeValueEnd}
                        onRelativeValueEndChange={(value) => {
                          updateCondition(block.id, condition.id, { relativeValueEnd: value });
                        }}
                        relativeDirectionEnd={condition.relativeDirectionEnd || 'ago'}
                        onRelativeDirectionEndChange={(direction) => {
                          updateCondition(block.id, condition.id, { relativeDirectionEnd: direction });
                        }}
                        applyAttempted={applyAttempted}
                      />
                    ) : isBetween ? (
                      <div className="flex space-x-2 flex-1">
                        <Input
                          type={isNumber ? 'number' : 'datetime-local'}
                          value={condition.value || ''}
                          onChange={(e) => updateCondition(block.id, condition.id, 'value', e.target.value)}
                          className="flex-1"
                          placeholder={isNumber ? 'From' : undefined}
                        />
                        <Input
                          type={isNumber ? 'number' : 'datetime-local'}
                          value={condition.valueEnd || ''}
                          onChange={(e) => updateCondition(block.id, condition.id, 'valueEnd', e.target.value)}
                          className="flex-1"
                          placeholder={isNumber ? 'To' : undefined}
                        />
                      </div>
                    ) : (() => {
                      const acOptions = autocompleteOptionsMap[fieldValue];
                      if (acOptions && acOptions.length > 0 && !isNumber) {
                        return (
                          <Combobox
                            options={acOptions}
                            value={condition.value || ''}
                            onChange={(val) => updateCondition(block.id, condition.id, 'value', val)}
                            placeholder="Select value..."
                            allowCustom
                          />
                        );
                      }
                      return (
                        <Input
                          type={isNumber ? 'number' : 'text'}
                          className="flex-1"
                          placeholder="Value"
                          value={condition.value || ''}
                          onChange={(e) => updateCondition(block.id, condition.id, 'value', e.target.value)}
                        />
                      );
                    })()}

                    {showMatchYearToggle && (
                      <input
                        type="text"
                        className="h-8 w-[120px] rounded-md border border-input bg-background px-2 text-xs"
                        placeholder="Any label"
                        value={condition.label || ''}
                        onChange={(e) =>
                          updateCondition(block.id, condition.id, { label: e.target.value || undefined })
                        }
                        title="Optional: only match dates with this label (e.g. birthday)"
                      />
                    )}

                    {showMatchYearToggle && (
                      <label className="flex items-center gap-1.5 text-xs text-muted-foreground whitespace-nowrap select-none">
                        <Checkbox
                          checked={!!condition.matchYear}
                          onCheckedChange={(checked) =>
                            updateCondition(block.id, condition.id, { matchYear: checked === true })
                          }
                        />
                        Match exact year
                      </label>
                    )}

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeCondition(block.id, condition.id)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                );
              })}
              {(() => {
                const neqByField = block.conditions
                  .filter(c => c.operator === 'neq')
                  .reduce((acc, c) => {
                    const key = c.table === 'custom_field' ? c.field : `${c.table}.${c.field}`;
                    acc[key] = (acc[key] || 0) + 1;
                    return acc;
                  }, {} as Record<string, number>);

                const hasOrNeqConflict = block.type === 'OR' &&
                  Object.values(neqByField).some(count => count >= 2);

                return hasOrNeqConflict ? (
                  <div className="flex items-start gap-2 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-xs mt-2">
                    <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <span>
                      Multiple "does not equal" conditions on the same field with OR will match all contacts.
                      Switch to <strong>AND</strong> to exclude these values.
                    </span>
                  </div>
                ) : null;
              })()}
              <Button variant="outline" size="sm" onClick={() => addCondition(block.id)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Condition
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Sort configuration */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <ArrowUpDown className="h-4 w-4" />
            Sort Order
          </h3>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setStagedSort([...stagedSort, { field: 'next_contact', direction: 'ASC', nulls: 'first' }])}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Sort
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-muted-foreground whitespace-nowrap">Preset:</label>
          <Select
            value={detectSortPreset(stagedSort)}
            onValueChange={(key) => {
              const preset = SORT_PRESETS[key];
              if (!preset) return; // 'custom' is display-only; not selectable
              // Picking Rotation collapses to empty so the server fallback drives the order;
              // explicit presets put the entries on screen so the user can edit/extend them.
              setStagedSort(key === 'rotation' ? [] : preset.entries.map((e) => ({ ...e })));
            }}
          >
            <SelectTrigger className="w-[260px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(SORT_PRESETS).map(([key, preset]) => (
                <SelectItem key={key} value={key}>
                  {preset.label}
                </SelectItem>
              ))}
              {detectSortPreset(stagedSort) === 'custom' && (
                <SelectItem value="custom" disabled>
                  Custom
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>
        {stagedSort.length > 0 && (
          <div className="space-y-2">
            {stagedSort.map((sortItem, index) => (
              <div key={index} className="flex items-center space-x-2">
                <Select
                  value={sortItem.field}
                  onValueChange={(value) => {
                    const updated = [...stagedSort];
                    updated[index] = { ...updated[index], field: value };
                    setStagedSort(updated);
                  }}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SORTABLE_FIELDS.map((f) => (
                      <SelectItem key={f.value} value={f.value}>
                        {f.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={sortItem.direction}
                  onValueChange={(value: 'ASC' | 'DESC') => {
                    const updated = [...stagedSort];
                    updated[index] = { ...updated[index], direction: value };
                    setStagedSort(updated);
                  }}
                >
                  <SelectTrigger className="w-[120px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ASC">Ascending</SelectItem>
                    <SelectItem value="DESC">Descending</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setStagedSort(stagedSort.filter((_, i) => i !== index))}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
        {stagedSort.length === 0 && (
          <p className="text-sm text-muted-foreground">
            Default: rotation order (unscheduled first, then by next contact date)
          </p>
        )}
      </div>

      <div className="flex justify-end space-x-2">
        <Button variant="outline" onClick={resetFilters}>
          Reset
        </Button>
        <Button onClick={applyFilters}>
          Apply Filters
        </Button>
      </div>
    </div>
  );
}